export interface RecordingResult {
  blob: Blob;
  url: string;
  duration: number;
  sizeBytes: number;
}

export class MeetingRecorder {
  private mediaRecorder: MediaRecorder | null = null;
  private recordedChunks: Blob[] = [];
  private recordingStream: MediaStream | null = null;
  private audioContext: AudioContext | null = null;
  private audioDestination: MediaStreamAudioDestinationNode | null = null;
  private startTime: number = 0;
  private timerInterval: number | null = null;
  private canvasTicker: number | null = null;
  private _isRecording: boolean = false;
  private _duration: number = 0;
  private lastResult: RecordingResult | null = null;

  public onTick?: (seconds: number) => void;
  public onStop?: (result: RecordingResult) => void;

  public isRecording(): boolean {
    return this._isRecording;
  }

  public getDuration(): number {
    return this._duration;
  }

  public getLastResult(): RecordingResult | null {
    return this.lastResult;
  }

  public clearLastResult() {
    this.lastResult = null;
  }

  public async start(localStream?: MediaStream | null): Promise<boolean> {
    if (this._isRecording) return false;

    let displayStream: MediaStream | null = null;

    // 1. Attempt display media capture (screen/tab/window) with audio
    if (navigator.mediaDevices && navigator.mediaDevices.getDisplayMedia) {
      try {
        displayStream = await navigator.mediaDevices.getDisplayMedia({
          video: {
            displaySurface: 'browser',
            frameRate: { ideal: 30, max: 60 },
          } as MediaTrackConstraints,
          audio: true,
        });
      } catch {
        // Fallback without audio if display audio is not supported on this OS/surface
        try {
          displayStream = await navigator.mediaDevices.getDisplayMedia({
            video: { frameRate: { ideal: 30, max: 60 } } as MediaTrackConstraints,
          });
        } catch (screenErr) {
          console.warn('getDisplayMedia denied or unsupported, falling back:', screenErr);
        }
      }
    }

    // 2. Fallback to local camera or canvas stream if screen capture is unavailable
    if (!displayStream || displayStream.getVideoTracks().length === 0) {
      if (localStream && localStream.getVideoTracks().length > 0) {
        displayStream = new MediaStream([...localStream.getVideoTracks()]);
      } else {
        const canvas = document.createElement('canvas');
        canvas.width = 1280;
        canvas.height = 720;
        const ctx = canvas.getContext('2d');
        if (ctx) {
          ctx.fillStyle = '#0b0f19';
          ctx.fillRect(0, 0, 1280, 720);
          ctx.fillStyle = '#6366f1';
          ctx.font = 'bold 36px sans-serif';
          ctx.textAlign = 'center';
          ctx.fillText('Verve In-Session Recording', 640, 360);
        }
        if ((canvas as any).captureStream) {
          displayStream = (canvas as any).captureStream(15);
          // Canvas captureStream only emits frames when drawn to; run a continuous ticker
          this.canvasTicker = window.setInterval(() => {
            if (!this._isRecording || !ctx) {
              if (this.canvasTicker) clearInterval(this.canvasTicker);
              return;
            }
            const sec = Math.floor((Date.now() - this.startTime) / 1000);
            ctx.fillStyle = '#0b0f19';
            ctx.fillRect(0, 0, 1280, 720);
            ctx.fillStyle = '#6366f1';
            ctx.font = 'bold 38px sans-serif';
            ctx.textAlign = 'center';
            ctx.fillText('Verve In-Session Recording', 640, 330);
            ctx.fillStyle = '#94a3b8';
            ctx.font = '24px sans-serif';
            ctx.fillText(`Recording duration: ${sec}s`, 640, 390);
          }, 1000 / 15);
        }
      }
    }

    if (!displayStream || displayStream.getVideoTracks().length === 0) {
      return false;
    }

    try {
      this.recordedChunks = [];
      const AudioCtxClass = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
      this.audioContext = new AudioCtxClass();
      this.audioDestination = this.audioContext.createMediaStreamDestination();

      // Mix display audio if present
      if (displayStream.getAudioTracks().length > 0) {
        try {
          const displayAudioSource = this.audioContext.createMediaStreamSource(displayStream);
          displayAudioSource.connect(this.audioDestination);
        } catch (e) {
          console.warn('Could not mix display audio:', e);
        }
      }

      // Mix local microphone if available
      if (localStream && localStream.getAudioTracks().length > 0) {
        try {
          const micSource = this.audioContext.createMediaStreamSource(localStream);
          micSource.connect(this.audioDestination);
        } catch (e) {
          console.warn('Could not mix local mic into recording:', e);
        }
      }

      // Mix all remote audio elements in DOM
      const remoteAudios = document.querySelectorAll<HTMLAudioElement>('audio');
      remoteAudios.forEach((audioEl) => {
        try {
          if (audioEl.srcObject instanceof MediaStream && audioEl.srcObject.getAudioTracks().length > 0) {
            const remoteSource = this.audioContext!.createMediaStreamSource(audioEl.srcObject);
            remoteSource.connect(this.audioDestination!);
          }
        } catch (e) {
          console.warn('Could not mix remote audio track into recording:', e);
        }
      });

      // Construct final recording composite stream: screen video + mixed audio
      const tracks: MediaStreamTrack[] = [
        ...displayStream.getVideoTracks(),
        ...this.audioDestination.stream.getAudioTracks(),
      ];

      this.recordingStream = new MediaStream(tracks);

      // Select best supported codec
      const mimeTypes = [
        'video/webm;codecs=vp9,opus',
        'video/webm;codecs=vp8,opus',
        'video/webm',
        'video/mp4',
      ];
      const selectedMime = mimeTypes.find(m => MediaRecorder.isTypeSupported(m)) || '';

      this.mediaRecorder = new MediaRecorder(this.recordingStream, selectedMime ? { mimeType: selectedMime } : {});

      this.mediaRecorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) {
          this.recordedChunks.push(event.data);
        }
      };

      this.mediaRecorder.onstop = () => {
        const mime = this.mediaRecorder?.mimeType || 'video/webm';
        const blob = new Blob(this.recordedChunks, { type: mime });
        const url = URL.createObjectURL(blob);
        const result: RecordingResult = {
          blob,
          url,
          duration: this._duration,
          sizeBytes: blob.size,
        };

        this.lastResult = result;
        this.cleanup();
        if (this.onStop) {
          this.onStop(result);
        }
      };

      // Handle user clicking "Stop Sharing" on browser bar
      displayStream.getVideoTracks()[0].onended = () => {
        if (this._isRecording) {
          this.stop();
        }
      };

      this.mediaRecorder.start(1000); // 1-second chunks for smooth recording
      this._isRecording = true;
      this.startTime = Date.now();
      this._duration = 0;

      this.timerInterval = window.setInterval(() => {
        this._duration = Math.floor((Date.now() - this.startTime) / 1000);
        if (this.onTick) this.onTick(this._duration);
      }, 1000);

      return true;
    } catch (err) {
      console.warn('Failed to start meeting recording:', err);
      this.cleanup();
      return false;
    }
  }

  public stop(): Promise<RecordingResult | null> {
    return new Promise((resolve) => {
      if (!this._isRecording || !this.mediaRecorder) {
        resolve(null);
        return;
      }

      const prevOnStop = this.onStop;
      this.onStop = (result) => {
        if (prevOnStop) prevOnStop(result);
        resolve(result);
      };

      try {
        if (this.mediaRecorder.state !== 'inactive') {
          this.mediaRecorder.stop();
        }
      } catch (err) {
        console.warn('Error stopping MediaRecorder:', err);
        this.cleanup();
        resolve(null);
      }
    });
  }

  private cleanup() {
    this._isRecording = false;
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
      this.timerInterval = null;
    }
    if (this.canvasTicker) {
      clearInterval(this.canvasTicker);
      this.canvasTicker = null;
    }
    if (this.recordingStream) {
      this.recordingStream.getTracks().forEach(t => t.stop());
      this.recordingStream = null;
    }
    if (this.audioContext && this.audioContext.state !== 'closed') {
      this.audioContext.close().catch(() => {});
      this.audioContext = null;
    }
    this.audioDestination = null;
  }
}
